import type { ReactNode } from "react";

import { CheckCircleIcon, GearIcon, XIcon } from "@phosphor-icons/react";
import {
  Box,
  Button,
  Dialog,
  Flex,
  Grid,
  IconButton,
  Popover,
  ScrollArea,
  SegmentedControl,
  Select,
  Switch,
  Text,
  TextField,
} from "@radix-ui/themes";

import { Field } from "~/v3-poc/components/Field";
import type { OrderLeg } from "../../shared/optionLegs/optionLeg.types";
import type { ReviewPresentation } from "../../shared/workspace/workspace.types";
import type {
  OrderReviewSnapshot,
  OrderTicketStep,
  TicketOrderType,
  TicketTimeInForce,
  TimeInForceOption,
} from "./OrderTicketWidget.types";

const UNAVAILABLE = "—";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCurrency = (value: number | null | undefined) =>
  typeof value === "number" && Number.isFinite(value)
    ? currencyFormatter.format(value)
    : UNAVAILABLE;

const fmt = (value: number | null | undefined) =>
  typeof value === "number" && Number.isFinite(value) ? value.toFixed(2) : "-";

const TIME_IN_FORCE_LABELS: Record<TicketTimeInForce, string> = {
  day: "Good for day",
  gtc: "Good till canceled",
  ioc: "Immediate or cancel",
  fok: "Fill or kill",
  opg: "At the open",
  cls: "At the close",
};

const ORDER_TYPE_LABELS: Record<TicketOrderType, string> = {
  market: "Market",
  limit: "Limit",
};

export interface OrderTicketWidgetPresentationProps {
  mode: "empty" | "equity" | "singleOption" | "multileg";
  /** Effective workspace symbol — group symbol when grouped, else retained local. */
  symbol?: string;
  /** Company name for the effective symbol, when the asset lookup has one. */
  assetName?: string;
  legs: OrderLeg[];
  quote?: { bid?: number | null; ask?: number | null; last?: number | null };
  /** The search node is composed by the connector (AssetSearch connector). */
  symbolSearch: ReactNode;
  side: "buy" | "sell";
  setSide: (side: "buy" | "sell") => void;
  quantity: number;
  setQuantity: (quantity: number) => void;
  orderType: "market" | "limit";
  setOrderType: (orderType: "market" | "limit") => void;
  limitPrice?: number;
  setLimitPrice: (limitPrice: number | undefined) => void;
  timeInForce: TicketTimeInForce;
  setTimeInForce: (timeInForce: TicketTimeInForce) => void;
  /** Narrowed to day/gtc for a spread, which is all the multileg endpoint takes. */
  timeInForceOptions: TimeInForceOption[];
  estimatedCost?: number;
  /** Why the ticket cannot be submitted yet, shown under the review button. */
  validationMessage?: string;
  /** Field-level message for the Quantity input, from the Zod schema. */
  quantityError?: string;
  /** Field-level message for the Limit price input, from the Zod schema. */
  limitPriceError?: string;
  groupSymbolError?: string;
  /** Removes the leg at this index from the ticket. */
  onRemoveLeg: (legIndex: number) => void;
  onInteract: () => void;
  step: OrderTicketStep;
  reviewSnapshot?: OrderReviewSnapshot;
  submitError?: string;
  submittedOrderId?: string;
  /** When false the primary action submits straight away, skipping review. */
  reviewEnabled: boolean;
  /** Whether review and receipt replace the editor or open over it. */
  reviewPresentation: ReviewPresentation;
  setReviewEnabled: (value: boolean) => void;
  setReviewPresentation: (value: ReviewPresentation) => void;
  /** The single submit entry point — reviews or submits per `reviewEnabled`. */
  onPrimary: () => void;
  onBackToEdit: () => void;
  onConfirm: () => void;
  onNewOrder: () => void;
  canReview: boolean;
}

// ---------------------------------------------------------------------------
// Buy/Sell toggle — semantic buttons with aria-pressed so the integration
// tests can address them by role="button". Radix's SegmentedControl would
// render role="radio" instead and break the accessible-name contract in
// Workspace.integration.test.tsx (`getByRole("button", { name: "Buy" })`).
// The visual state is driven by variant + color, not a bespoke class map.
// ---------------------------------------------------------------------------

const SideToggle = ({
  side,
  setSide,
}: {
  side: "buy" | "sell";
  setSide: (next: "buy" | "sell") => void;
}) => (
  <Flex gap="2">
    <Button
      type="button"
      size="3"
      className="flex-1"
      aria-pressed={side === "buy"}
      variant={side === "buy" ? "soft" : "outline"}
      color={side === "buy" ? "green" : "gray"}
      onClick={() => setSide("buy")}
    >
      Buy
    </Button>
    <Button
      type="button"
      size="3"
      className="flex-1"
      aria-pressed={side === "sell"}
      variant={side === "sell" ? "soft" : "outline"}
      color={side === "sell" ? "red" : "gray"}
      onClick={() => setSide("sell")}
    >
      Sell
    </Button>
  </Flex>
);

// ---------------------------------------------------------------------------
// Leg row — a symbol, a Buy/Sell tag coloured by quoteSide, and a remove
// IconButton. `sideForQuoteSide` in the controller is `ask → buy, bid → sell`;
// this preserves that mapping in the visible label.
// ---------------------------------------------------------------------------

const LegRow = ({
  leg,
  legIndex,
  onRemoveLeg,
}: {
  leg: OrderLeg;
  legIndex: number;
  onRemoveLeg: (index: number) => void;
}) => {
  const intent = leg.quoteSide === "ask" ? "Buy" : "Sell";
  return (
    <Flex
      asChild
      align="center"
      justify="between"
      gap="2"
      px="2"
      py="1"
      className="rounded-[var(--radius-2)] border border-[color:var(--gray-a5)]"
    >
      <li>
        <Text size="1" truncate className="atd-numeric min-w-0 flex-1">
          {leg.contract.symbol}
        </Text>
        <Text size="1" color={leg.quoteSide === "ask" ? "green" : "red"}>
          {intent}
        </Text>
        <IconButton
          type="button"
          size="1"
          variant="ghost"
          color="gray"
          aria-label={`Remove leg ${leg.contract.symbol}`}
          onClick={() => onRemoveLeg(legIndex)}
        >
          <XIcon size={12} aria-hidden />
        </IconButton>
      </li>
    </Flex>
  );
};

// ---------------------------------------------------------------------------
// Review summary
// ---------------------------------------------------------------------------

const SummaryRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Flex align="center" justify="between" gap="3">
    <Text size="2" color="gray">
      {label}
    </Text>
    <Text size="2" weight="medium" className="atd-numeric">
      {children}
    </Text>
  </Flex>
);

const summarySymbol = (snapshot: OrderReviewSnapshot) =>
  snapshot.mode === "singleOption"
    ? snapshot.orderSymbol
    : snapshot.displaySymbol;

const OrderSummary = ({ snapshot }: { snapshot: OrderReviewSnapshot }) => (
  <Flex direction="column" gap="3">
    <SummaryRow label="Symbol">{summarySymbol(snapshot)}</SummaryRow>
    {snapshot.mode !== "multileg" && (
      <SummaryRow label="Side">
        {snapshot.side === "buy" ? "Buy" : "Sell"}
      </SummaryRow>
    )}
    <SummaryRow label="Quantity">{snapshot.quantity}</SummaryRow>
    <SummaryRow label="Order type">
      {ORDER_TYPE_LABELS[snapshot.orderType]}
    </SummaryRow>
    {snapshot.orderType === "limit" && snapshot.limitPrice !== undefined && (
      <SummaryRow label="Limit price">
        {formatCurrency(snapshot.limitPrice)}
      </SummaryRow>
    )}
    <SummaryRow label="Time in force">
      {TIME_IN_FORCE_LABELS[snapshot.timeInForce]}
    </SummaryRow>
    {snapshot.estimatedCost !== undefined && (
      <SummaryRow label="Est. cost">
        {formatCurrency(snapshot.estimatedCost)}
      </SummaryRow>
    )}
    {snapshot.mode === "multileg" && (
      <Flex asChild direction="column" gap="1">
        <ul className="m-0 list-none p-0">
          {snapshot.legs.map((leg) => (
            <Flex
              key={leg.symbol}
              asChild
              align="center"
              justify="between"
              gap="2"
              px="2"
              py="1"
              className="rounded-[var(--radius-2)] border border-[color:var(--gray-a5)]"
            >
              <li>
                <Text size="1" truncate className="atd-numeric min-w-0 flex-1">
                  {leg.symbol}
                </Text>
                <Text size="1" color={leg.side === "buy" ? "green" : "red"}>
                  {leg.side === "buy" ? "Buy" : "Sell"}
                </Text>
              </li>
            </Flex>
          ))}
        </ul>
      </Flex>
    )}
  </Flex>
);

const ReviewBody = ({
  step,
  snapshot,
  submitError,
  onBackToEdit,
  onConfirm,
}: {
  step: "reviewing" | "submitting";
  snapshot: OrderReviewSnapshot;
  submitError?: string;
  onBackToEdit: () => void;
  onConfirm: () => void;
}) => {
  const isSubmitting = step === "submitting";
  const heading =
    snapshot.mode === "multileg"
      ? `Review ${snapshot.legs.length}-leg order`
      : `Review ${snapshot.side === "buy" ? "Buy" : "Sell"} ${snapshot.displaySymbol}`;
  const confirmLabel = submitError
    ? "Retry"
    : snapshot.mode === "multileg"
      ? "Confirm order"
      : `Confirm ${snapshot.side}`;

  return (
    <Flex direction="column" gap="4">
      <Text size="5" weight="bold">
        {heading}
      </Text>
      <OrderSummary snapshot={snapshot} />
      {submitError && (
        <Text role="alert" size="2" color="red">
          {submitError}
        </Text>
      )}
      <Flex direction="column" gap="2">
        <Button
          type="button"
          size="3"
          loading={isSubmitting}
          disabled={isSubmitting}
          onClick={onConfirm}
        >
          {isSubmitting ? "Submitting…" : confirmLabel}
        </Button>
        <Button
          type="button"
          variant="soft"
          color="gray"
          disabled={isSubmitting}
          onClick={onBackToEdit}
        >
          Back
        </Button>
      </Flex>
    </Flex>
  );
};

const SuccessBody = ({
  snapshot,
  submittedOrderId,
  onNewOrder,
}: {
  snapshot: OrderReviewSnapshot;
  submittedOrderId?: string;
  onNewOrder: () => void;
}) => (
  <Flex direction="column" gap="4">
    <Flex direction="column" align="center" gap="2" py="3">
      <CheckCircleIcon size={32} color="var(--green-9)" aria-hidden />
      <Text size="5" weight="bold">
        Order submitted
      </Text>
      {submittedOrderId && (
        <Text size="1" color="gray" className="atd-numeric">
          Order ID {submittedOrderId}
        </Text>
      )}
    </Flex>
    <OrderSummary snapshot={snapshot} />
    <Button type="button" size="3" onClick={onNewOrder}>
      New order
    </Button>
  </Flex>
);

// ---------------------------------------------------------------------------
// Ticket settings
// ---------------------------------------------------------------------------

const TicketSettingsPopover = ({
  reviewEnabled,
  reviewPresentation,
  setReviewEnabled,
  setReviewPresentation,
}: Pick<
  OrderTicketWidgetPresentationProps,
  | "reviewEnabled"
  | "reviewPresentation"
  | "setReviewEnabled"
  | "setReviewPresentation"
>) => (
  <Popover.Root>
    <Popover.Trigger>
      <IconButton
        type="button"
        variant="ghost"
        color="gray"
        aria-label="Ticket settings"
      >
        <GearIcon />
      </IconButton>
    </Popover.Trigger>
    <Popover.Content size="1" width="260px">
      <Flex direction="column" gap="3">
        <Text as="label" size="2">
          <Flex align="center" justify="between" gap="2">
            Review before submit
            <Switch
              checked={reviewEnabled}
              onCheckedChange={setReviewEnabled}
            />
          </Flex>
        </Text>

        <Flex direction="column" gap="1">
          <Text size="2" color="gray">
            Review style
          </Text>
          <SegmentedControl.Root
            size="1"
            aria-label="Review style"
            value={reviewPresentation}
            onValueChange={(value) =>
              setReviewPresentation(value as ReviewPresentation)
            }
          >
            <SegmentedControl.Item value="inline">Inline</SegmentedControl.Item>
            <SegmentedControl.Item value="modal">Modal</SegmentedControl.Item>
          </SegmentedControl.Root>
        </Flex>
      </Flex>
    </Popover.Content>
  </Popover.Root>
);

// ---------------------------------------------------------------------------
// Editor body
// ---------------------------------------------------------------------------

const EditorBody = ({
  mode,
  symbol,
  assetName,
  legs,
  quote,
  side,
  setSide,
  quantity,
  setQuantity,
  orderType,
  setOrderType,
  limitPrice,
  setLimitPrice,
  timeInForce,
  setTimeInForce,
  timeInForceOptions,
  estimatedCost,
  validationMessage,
  quantityError,
  limitPriceError,
  onRemoveLeg,
  onPrimary,
  canReview,
  reviewEnabled,
}: Pick<
  OrderTicketWidgetPresentationProps,
  | "mode"
  | "symbol"
  | "assetName"
  | "legs"
  | "quote"
  | "side"
  | "setSide"
  | "quantity"
  | "setQuantity"
  | "orderType"
  | "setOrderType"
  | "limitPrice"
  | "setLimitPrice"
  | "timeInForce"
  | "setTimeInForce"
  | "timeInForceOptions"
  | "estimatedCost"
  | "validationMessage"
  | "quantityError"
  | "limitPriceError"
  | "onRemoveLeg"
  | "onPrimary"
  | "canReview"
  | "reviewEnabled"
>) => {
  const headerSymbol =
    mode === "multileg"
      ? `${legs.length} legs${symbol ? ` · ${symbol}` : ""}`
      : symbol || "No symbol";
  const lastPrice = mode === "multileg" ? undefined : quote?.last;
  const primaryLabel =
    mode === "multileg"
      ? reviewEnabled
        ? "Review order"
        : "Place order"
      : `${reviewEnabled ? "Review " : ""}${side} ${symbol ?? ""}`.trim();
  // Field-level errors already surface the quantity/limit-price message next
  // to the input that caused it, so the banner below only has to speak up for
  // a gate no single field owns (no symbol, no account).
  const showValidationBanner =
    !!validationMessage &&
    validationMessage !== quantityError &&
    validationMessage !== limitPriceError;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onPrimary();
      }}
      className="flex min-h-0 flex-1 flex-col"
    >
      <ScrollArea type="auto" scrollbars="vertical" className="min-h-0 flex-1">
        <Flex direction="column" gap="4" p="1">
          <Flex align="start" justify="between" gap="3">
            <Box minWidth="0">
              <Text as="div" size="5" weight="bold" truncate>
                {headerSymbol}
              </Text>
              {assetName && (
                <Text as="p" m="0" size="1" color="gray" truncate>
                  {assetName}
                </Text>
              )}
            </Box>

            {mode !== "multileg" && quote && (
              <Flex direction="column" align="end" flexShrink="0">
                <Text size="5" weight="bold" className="atd-numeric">
                  {formatCurrency(lastPrice)}
                </Text>
                <Text size="1" color="gray" className="atd-numeric">
                  bid {fmt(quote.bid)} / ask {fmt(quote.ask)}
                </Text>
              </Flex>
            )}
          </Flex>

          {mode === "multileg" && (
            <Flex asChild direction="column" gap="1">
              <ul className="m-0 list-none p-0">
                {legs.map((leg, legIndex) => (
                  <LegRow
                    key={leg.contract.symbol}
                    leg={leg}
                    legIndex={legIndex}
                    onRemoveLeg={onRemoveLeg}
                  />
                ))}
              </ul>
            </Flex>
          )}

          {mode !== "multileg" && <SideToggle side={side} setSide={setSide} />}

          <Grid columns="2" gap="3">
            <Field label="Quantity" error={quantityError}>
              {(control) => (
                <TextField.Root
                  {...control}
                  className="atd-numeric"
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(event) => setQuantity(Number(event.target.value))}
                />
              )}
            </Field>

            <Field label="Order type">
              {(control) => (
                <Select.Root
                  value={orderType}
                  onValueChange={(next) =>
                    setOrderType(next as "market" | "limit")
                  }
                >
                  <Select.Trigger
                    id={control.id}
                    aria-describedby={control["aria-describedby"]}
                    aria-invalid={control["aria-invalid"]}
                  />
                  <Select.Content>
                    <Select.Item value="market">Market</Select.Item>
                    <Select.Item value="limit">Limit</Select.Item>
                  </Select.Content>
                </Select.Root>
              )}
            </Field>

            {orderType === "limit" && (
              <Field label="Limit price" error={limitPriceError}>
                {(control) => (
                  <TextField.Root
                    {...control}
                    className="atd-numeric"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={limitPrice ?? ""}
                    onChange={(event) =>
                      setLimitPrice(
                        event.target.value === ""
                          ? undefined
                          : Number(event.target.value),
                      )
                    }
                  />
                )}
              </Field>
            )}

            <Field label="Time in force">
              {(control) => (
                <Select.Root
                  value={timeInForce}
                  onValueChange={(next) =>
                    setTimeInForce(next as TicketTimeInForce)
                  }
                >
                  <Select.Trigger
                    id={control.id}
                    aria-describedby={control["aria-describedby"]}
                    aria-invalid={control["aria-invalid"]}
                  />
                  <Select.Content>
                    {timeInForceOptions.map((option) => (
                      <Select.Item key={option.value} value={option.value}>
                        {option.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              )}
            </Field>
          </Grid>

          <Flex
            align="center"
            justify="between"
            gap="2"
            pt="3"
            className="border-[color:var(--gray-a5)] border-t"
          >
            <Text size="2" color="gray">
              Est. cost
            </Text>
            <Text size="4" weight="bold" className="atd-numeric">
              {formatCurrency(estimatedCost)}
            </Text>
          </Flex>

          <Flex direction="column" gap="2">
            <Button type="submit" size="3" disabled={!canReview}>
              {primaryLabel}
            </Button>

            {showValidationBanner && (
              <Text size="1" color="red" role="alert">
                {validationMessage}
              </Text>
            )}
          </Flex>
        </Flex>
      </ScrollArea>
    </form>
  );
};

// ---------------------------------------------------------------------------
// Presentation
// ---------------------------------------------------------------------------

/** Pure order-ticket presentation — all data via props, no hooks. */
export const OrderTicketWidgetPresentation = ({
  mode,
  symbol,
  assetName,
  legs,
  quote,
  symbolSearch,
  side,
  setSide,
  quantity,
  setQuantity,
  orderType,
  setOrderType,
  limitPrice,
  setLimitPrice,
  timeInForce,
  setTimeInForce,
  timeInForceOptions,
  estimatedCost,
  validationMessage,
  quantityError,
  limitPriceError,
  groupSymbolError,
  onRemoveLeg,
  onInteract,
  step,
  reviewSnapshot,
  submitError,
  submittedOrderId,
  reviewEnabled,
  reviewPresentation,
  setReviewEnabled,
  setReviewPresentation,
  onPrimary,
  onBackToEdit,
  onConfirm,
  onNewOrder,
  canReview,
}: OrderTicketWidgetPresentationProps) => {
  const reviewOpen =
    !!reviewSnapshot && (step === "reviewing" || step === "submitting");
  const successOpen = !!reviewSnapshot && step === "success";

  const editor =
    mode === "empty" ? (
      <Text size="1" color="gray">
        Pick a symbol, assign a group, or click an option contract.
      </Text>
    ) : (
      <EditorBody
        mode={mode}
        symbol={symbol}
        assetName={assetName}
        legs={legs}
        quote={quote}
        side={side}
        setSide={setSide}
        quantity={quantity}
        setQuantity={setQuantity}
        orderType={orderType}
        setOrderType={setOrderType}
        limitPrice={limitPrice}
        setLimitPrice={setLimitPrice}
        timeInForce={timeInForce}
        setTimeInForce={setTimeInForce}
        timeInForceOptions={timeInForceOptions}
        estimatedCost={estimatedCost}
        validationMessage={validationMessage}
        quantityError={quantityError}
        limitPriceError={limitPriceError}
        onRemoveLeg={onRemoveLeg}
        onPrimary={onPrimary}
        canReview={canReview}
        reviewEnabled={reviewEnabled}
      />
    );

  // The gear sits beside the search rather than in the editor so a ticket can
  // be configured before it has a symbol, and while a review is on screen.
  const header = (
    <Flex direction="column" gap="1" flexShrink="0">
      <Flex align="center" gap="2">
        <Box flexGrow="1" minWidth="0">
          {symbolSearch}
        </Box>
        <TicketSettingsPopover
          reviewEnabled={reviewEnabled}
          reviewPresentation={reviewPresentation}
          setReviewEnabled={setReviewEnabled}
          setReviewPresentation={setReviewPresentation}
        />
      </Flex>
      {groupSymbolError && (
        <Text role="alert" size="1" color="red">
          {groupSymbolError}
        </Text>
      )}
    </Flex>
  );

  // Modal keeps the draft on screen behind a focus-trapped dialog, so the user
  // can read what they typed while confirming. `open` stays derived from the
  // step, which is what makes an Escape during submit a no-op rather than a
  // way to lose sight of an in-flight order.
  if (reviewPresentation === "modal") {
    return (
      <Flex
        direction="column"
        gap="3"
        p="3"
        height="100%"
        minHeight="0"
        onFocusCapture={onInteract}
      >
        {header}
        {editor}

        <Dialog.Root
          open={reviewOpen}
          onOpenChange={(open) => {
            if (!open && step === "reviewing") {
              onBackToEdit();
            }
          }}
        >
          <Dialog.Content maxWidth="420px">
            {reviewSnapshot && (
              <ReviewBody
                step={step === "submitting" ? "submitting" : "reviewing"}
                snapshot={reviewSnapshot}
                submitError={submitError}
                onBackToEdit={onBackToEdit}
                onConfirm={onConfirm}
              />
            )}
          </Dialog.Content>
        </Dialog.Root>

        <Dialog.Root
          open={successOpen}
          onOpenChange={(open) => {
            if (!open) {
              onNewOrder();
            }
          }}
        >
          <Dialog.Content maxWidth="420px">
            {reviewSnapshot && (
              <SuccessBody
                snapshot={reviewSnapshot}
                submittedOrderId={submittedOrderId}
                onNewOrder={onNewOrder}
              />
            )}
          </Dialog.Content>
        </Dialog.Root>
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      gap="3"
      p="3"
      height="100%"
      minHeight="0"
      onFocusCapture={onInteract}
    >
      {header}

      {successOpen && reviewSnapshot ? (
        <ScrollArea
          type="auto"
          scrollbars="vertical"
          className="min-h-0 flex-1"
        >
          <SuccessBody
            snapshot={reviewSnapshot}
            submittedOrderId={submittedOrderId}
            onNewOrder={onNewOrder}
          />
        </ScrollArea>
      ) : reviewOpen && reviewSnapshot ? (
        <ScrollArea
          type="auto"
          scrollbars="vertical"
          className="min-h-0 flex-1"
        >
          <ReviewBody
            step={step === "submitting" ? "submitting" : "reviewing"}
            snapshot={reviewSnapshot}
            submitError={submitError}
            onBackToEdit={onBackToEdit}
            onConfirm={onConfirm}
          />
        </ScrollArea>
      ) : step === "editing" ? (
        editor
      ) : null}
    </Flex>
  );
};
